import { FC, ReactNode } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";

import PatientHexAvatar from "./PatientHexAvatar";

export type PreviewActive = { name: string; conc: string };

const OBS_MAX_CHARS = 34;

export const truncateObservation = (value?: string, maxChars = OBS_MAX_CHARS): string => {
    const text = (value || "").trim();
    if (!text) return "";
    return text.length > maxChars ? `${text.slice(0, maxChars).trim()}...` : text;
};

type Props = {
    patientName: string;
    meta?: string;
    loading?: boolean;
    actives: PreviewActive[];
    vehicleName?: string | null;
    vehicleObservation?: string;
    observations?: string[];
    onClose: () => void;
    actions?: ReactNode;
    compact?: boolean;
};

export const ConsultationPreviewCard: FC<Props> = ({
    patientName,
    meta,
    loading,
    actives,
    vehicleName,
    vehicleObservation,
    observations = [],
    onClose,
    actions,
    compact = false,
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                p: compact ? "12px 14px" : "20px 22px",
                ...(compact && { "@media (min-height: 900px)": { p: "18px 20px" } }),
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: compact ? 1.25 : 2,
                    ...(compact && { "@media (min-height: 900px)": { mb: 1.75 } }),
                }}
            >
                <PatientHexAvatar name={patientName} size={compact ? 36 : 42} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary" }}>
                        {patientName}
                    </Typography>
                    {meta && (
                        <Typography noWrap sx={{ fontSize: "12px", color: "text.secondary" }}>
                            {meta}
                        </Typography>
                    )}
                </Box>
                <Tooltip title="Fechar" placement="top">
                    <IconButton aria-label="Fechar" onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
                        <CloseRounded sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {loading ? (
                <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "13px",
                            overflow: "hidden",
                            mb: vehicleName || observations.length > 0 ? 1.5 : 0,
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: compact ? 0.55 : 1,
                                ...(compact && { "@media (min-height: 900px)": { py: 0.8 } }),
                                backgroundColor: theme.palette.brand.surface2,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.6px", textTransform: "uppercase", color: "text.secondary" }}>
                                {`Formula - ${vehicleName ? `${vehicleName}` : "base nao informada"}${vehicleObservation ? ` obs: ${vehicleObservation}` : ""}`}
                            </Typography>
                        </Box>
                        {actives.length === 0 ? (
                            <Typography sx={{ px: 2, py: 1.5, fontSize: "13px", color: "text.secondary", fontStyle: "italic" }}>
                                Nenhum ativo nesta consulta.
                            </Typography>
                        ) : (
                            actives.map((a, idx) => (
                                <Box
                                    key={`${a.name}-${idx}`}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 1.5,
                                        px: 2,
                                        py: compact ? 0.5 : 1,
                                        ...(compact && { "@media (min-height: 900px)": { py: 0.75 } }),
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        "&:last-of-type": { borderBottom: "none" },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: compact ? "11.5px" : "13.5px",
                                            color: "text.primary",
                                            minWidth: 0,
                                            ...(compact && { "@media (min-height: 900px)": { fontSize: "13px" } }),
                                        }}
                                    >
                                        {a.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: compact ? "11.5px" : "13.5px",
                                            fontWeight: 600,
                                            color: "primary.main",
                                            flexShrink: 0,
                                            ...(compact && { "@media (min-height: 900px)": { fontSize: "13px" } }),
                                        }}
                                    >
                                        {a.conc}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>

                    {observations.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.6px", textTransform: "uppercase", color: "text.secondary", mb: 0.5 }}>
                                Observações
                            </Typography>
                            {observations.map((obs, idx) => (
                                <Typography key={idx} sx={{ fontSize: "13px", color: "text.primary", lineHeight: 1.6 }}>
                                    {obs}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </>
            )}

            {actions && (
                <Box sx={{ display: "flex", gap: 1, mt: compact ? 1.25 : 2 }}>
                    {actions}
                </Box>
            )}
        </Box>
    );
};

export default ConsultationPreviewCard;
